import React, { useState, useCallback } from 'react';
import { Button, TextField, Box, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Checkbox, FormControlLabel, CircularProgress } from '@mui/material';
import { uploadImage, describeImageFromBase64 } from '../../services/images';
import { useLang } from '../../contexts/langContext';
import { useDropzone } from 'react-dropzone';
import { AddAPhoto } from '@mui/icons-material';
import { Image } from '../../types';
import { useBranding } from '../../contexts/BrandingContext';

interface ImageUploadProps {
  onUploadSuccess: (image: Image) => void;
  buttonProps?: any;
}

function ImageUpload({ onUploadSuccess, buttonProps }: ImageUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [alt, setAlt] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [useAIDescription, setUseAIDescription] = useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const { lang } = useLang();
  const { features } = useBranding();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0];
    setFile(selectedFile);
    setAlt(selectedFile.name);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: {'image/*': []} });

  const generateAIDescription = async () => {
    if (!file) return;
    
    setIsGeneratingDescription(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve) => {
        reader.onload = () => {
          const base64 = reader.result as string;
          resolve(base64);
        };
      });
      reader.readAsDataURL(file);
      
      const base64Image = await base64Promise;
      const description = await describeImageFromBase64(base64Image, undefined, lang);
      setAlt(description);
    } catch (error) {
      console.error('Error generating AI description:', error);
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (file) {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('alt', alt);
      const createdImage = await uploadImage(formData);
      setFile(null);
      setAlt('');
      setUseAIDescription(false);
      setIsDialogOpen(false);
      onUploadSuccess(createdImage);
    }
  };

  React.useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  const handleOpenDialog = () => {
    setFile(null);
    setAlt('');
    setUseAIDescription(false);
    setIsDialogOpen(true);
  };

  // Generate description automatically when checkbox is checked and we have a file
  React.useEffect(() => {
    if (useAIDescription && file && alt === file.name) {
      generateAIDescription();
    }
  }, [useAIDescription, file]);

  return (
    <>
      <Button variant="contained" onClick={handleOpenDialog} startIcon={<AddAPhoto />} {...buttonProps}>
        {lang === 'en' ? 'Upload Image' : '上传图片'}
      </Button>

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
        <DialogTitle>{lang === 'en' ? 'Upload Image' : '上传图片'}</DialogTitle>
        <DialogContent>
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed #cccccc',
              borderRadius: 2,
              p: 2,
              mb: 2,
              textAlign: 'center',
              cursor: 'pointer',
            }}
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <Typography>{lang === 'en' ? 'Drop the image here' : '将图片拖放到这里'}</Typography>
            ) : (
              <Button variant="text">
                {lang === 'en' ? 'Drag and drop an image here, or click to select' : '拖放图片到这里，或点击此处选择文件'}
              </Button>
            )}
          </Box>
          {file && previewUrl && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <Box
                component="img"
                src={previewUrl}
                alt="Preview"
                sx={{
                  maxWidth: '200px',
                  maxHeight: '200px',
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
            </Box>
          )}
          <Typography variant="body2" sx={{ mb: 1 }} color="text.secondary">
            {file ? file.name : lang === 'en' ? 'No file selected' : '未选择文件'}
          </Typography>

          {file && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={useAIDescription}
                  onChange={(e) => setUseAIDescription(e.target.checked)}
                  disabled={isGeneratingDescription || !features.enableMultimodalModel}
                />
              }
              label={lang === 'en' ? 'Generate AI description' : '生成AI描述'}
              sx={{ mb: 2 }}
            />
          )}

          <TextField
            fullWidth
            label={lang === 'en' ? 'Description text' : '描述文本'}
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            sx={{ mb: 2 }}
            helperText={
              isGeneratingDescription
                ? lang === 'en'
                  ? 'Generating description...'
                  : '正在生成描述...'
                : alt === file?.name
                  ? lang === 'en'
                    ? 'Please provide a more descriptive text to help AI understand the image content and use'
                    : '请提供更详细的描述以帮助AI理解图片内容和用处'
                  : undefined
            }
            error={alt === file?.name && !isGeneratingDescription}
            disabled={isGeneratingDescription}
            InputProps={{
              endAdornment: isGeneratingDescription ? <CircularProgress size={20} /> : null,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>{lang === 'en' ? 'Cancel' : '取消'}</Button>
          {features?.enableMultimodalModel && file && !useAIDescription && alt === file.name && (
            <Button onClick={generateAIDescription} disabled={isGeneratingDescription}>
              {lang === 'en' ? 'Generate Description' : '生成描述'}
            </Button>
          )}
          <Button onClick={handleSubmit} disabled={!file || isGeneratingDescription}>
            {lang === 'en' ? 'Upload' : '上传'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ImageUpload;
